import { MessagePart, QuestionPageSpec } from './aiAssistantModel'
import {
  FormValues,
  buildSubmitValues,
  fieldErrors,
  initialFormValues,
  missingRequiredFields,
  submittedDisplayValue,
} from './formRequestModel'

/**
 * The pager's working answers, keyed by page id. An options page holds the
 * chosen option id (or is absent until chosen); a form page always holds its
 * value map (seeded up front). Skips are tracked separately so a skipped form
 * page keeps its entered values if the user changes their mind.
 */
export type QuestionSetAnswers = Record<string, string | FormValues | undefined>

/** Seed the working answers: form pages get their initial field values. */
export function initialAnswers(pages: QuestionPageSpec[]): QuestionSetAnswers {
  const answers: QuestionSetAnswers = {}
  for (const page of pages) {
    if (page.kind === 'form') {
      answers[page.page_id] = initialFormValues(page.fields)
    }
  }
  return answers
}

/**
 * Whether a page is ready to move past: skipped, an option chosen, or a form
 * with every required field satisfied and no format errors.
 */
export function isPageComplete(
  page: QuestionPageSpec,
  answers: QuestionSetAnswers,
  skipped: Set<string>
): boolean {
  if (skipped.has(page.page_id)) {
    return true
  }
  const answer = answers[page.page_id]
  if (page.kind === 'options') {
    return typeof answer === 'string'
  }
  const values = (answer ?? {}) as FormValues
  return (
    missingRequiredFields(page.fields, values).length === 0 &&
    Object.keys(fieldErrors(page.fields, values)).length === 0
  )
}

/** Whether every page is answered or skipped — gates the final submit. */
export function allPagesComplete(
  pages: QuestionPageSpec[],
  answers: QuestionSetAnswers,
  skipped: Set<string>
): boolean {
  return pages.every((page) => isPageComplete(page, answers, skipped))
}

/**
 * Errors to display for the current page: a form page's format errors
 * filtered to touched fields (mirroring the standalone form's behavior);
 * an options page never shows field errors.
 */
export function shownPageErrors(
  page: QuestionPageSpec,
  answers: QuestionSetAnswers,
  touched: Set<string>
): Record<string, string> {
  if (page.kind !== 'form') {
    return {}
  }
  const values = (answers[page.page_id] ?? {}) as FormValues
  const errors = fieldErrors(page.fields, values)
  const shown: Record<string, string> = {}
  for (const name of Object.keys(errors)) {
    if (touched.has(name)) {
      shown[name] = errors[name]
    }
  }
  return shown
}

/**
 * One-line summary of a page's answer for the review page: the chosen
 * option's label, "Skipped", or the form's non-blank values as
 * "Label: value" pairs. Empty when unanswered.
 */
export function pageAnswerSummary(
  page: QuestionPageSpec,
  answers: QuestionSetAnswers,
  skipped: Set<string>
): string {
  if (skipped.has(page.page_id)) {
    return 'Skipped'
  }
  const answer = answers[page.page_id]
  if (page.kind === 'options') {
    const option = page.options.find((o) => o.id === answer)
    return option ? option.label : ''
  }
  const values = (answer ?? {}) as FormValues
  const cleaned = buildSubmitValues(page.fields, values)
  return page.fields
    .filter((field) => cleaned[field.name] !== undefined && cleaned[field.name] !== '')
    .map((field) => `${field.label}: ${String(cleaned[field.name])}`)
    .join(', ')
}

/**
 * Build the batch for the question-sets endpoint: option ids as-is, form
 * values cleaned like a standalone form submit (numbers coerced, blanks
 * omitted, checkboxes always present), and an explicit null for every
 * skipped page.
 */
export function buildSubmitAnswers(
  pages: QuestionPageSpec[],
  answers: QuestionSetAnswers,
  skipped: Set<string>
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const page of pages) {
    if (skipped.has(page.page_id)) {
      out[page.page_id] = null
    } else if (page.kind === 'options') {
      out[page.page_id] = answers[page.page_id]
    } else {
      out[page.page_id] = buildSubmitValues(
        page.fields,
        (answers[page.page_id] ?? {}) as FormValues
      )
    }
  }
  return out
}

/** One "Label: value" line of a submitted form page. */
export type AnswerRow = { label: string; value: string }

/** A page's answer as recorded in history, ready to render read-only. */
export type ResolvedPageAnswer =
  | { kind: 'skipped' }
  | { kind: 'option'; label: string }
  | { kind: 'form'; rows: AnswerRow[] }

/**
 * Interpret one page's recorded answer. `null` means the user skipped the
 * page; an options page holds the chosen option id (shown as its label, or the
 * raw id if the option has since disappeared); a form page holds its value map.
 *
 * A malformed answer reads as skipped rather than throwing — a history should
 * render whatever it can.
 */
export function resolvedPageAnswer(
  page: QuestionPageSpec,
  answer: unknown
): ResolvedPageAnswer {
  if (answer === null || answer === undefined) {
    return { kind: 'skipped' }
  }
  if (page.kind === 'options') {
    const option = page.options.find((o) => o.id === answer)
    return { kind: 'option', label: option ? option.label : String(answer) }
  }
  if (typeof answer !== 'object') {
    return { kind: 'skipped' }
  }
  const values = answer as FormValues
  return {
    kind: 'form',
    rows: page.fields.map((field) => ({
      label: field.label,
      value: submittedDisplayValue(field, values[field.name]),
    })),
  }
}

/**
 * Whether a question set has been answered: during the pause only the
 * placeholder tool result (plain text) carries the set's id; once the user
 * submits, the harness replaces it in history and streams a second result
 * whose content is the structured `{"answers": [...]}` JSON. Used to lock
 * the pager so a set cannot be submitted twice.
 */
export function questionSetResolved(
  parts: MessagePart[],
  questionSetId: string
): boolean {
  return parts.some((part) => {
    if (part.type !== 'tool_result' || part.tool_call_id !== questionSetId) {
      return false
    }
    try {
      const data = JSON.parse(part.content)
      return typeof data === 'object' && data !== null && 'answers' in data
    } catch {
      return false
    }
  })
}
